import { useEffect, useRef, useState } from "react";
import { ApiError, api } from "../../lib/api";
import styles from "./LiveViewPlayer.module.css";

type LiveViewPlayerProps = {
  cameraId: string;
  cameraName: string;
  retryBaseMs?: number;
};

// Frames are motion-driven, not a continuous feed: after this long without a
// fresh frame we stop implying anything is happening (design §9.1).
const STALE_AFTER_MS = 12_000;
// The stream must outlive quiet periods and transient drops: reconnect forever
// (bounded exponential backoff) while the page is open. Only a 403 — no plan /
// not a member — is terminal.
const RETRY_MAX_MS = 30_000;
// Frames arrive in bundles (one upload carries ~7 s of footage), so rendering on
// arrival replays motion at network speed. Pace display at the capture rate and
// cap the backlog so live-ness never lags by more than ~2 bundles.
const FRAME_INTERVAL_MS = 333;
const MAX_QUEUED_FRAMES = 40;

const frameSrc = (data: string): string =>
  data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;

export const LiveViewPlayer = ({
  cameraId,
  cameraName,
  retryBaseMs = 1_000,
}: LiveViewPlayerProps) => {
  const [frame, setFrame] = useState<string | null>(null);
  // Portrait cameras (Orient 90/270): the box follows the frame's own aspect so
  // the full view is visible; 16/9 is only the pre-first-frame default.
  const [frameAspect, setFrameAspect] = useState<number | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const attemptsRef = useRef(0);
  const queueRef = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    let source: EventSource | null = null;
    let staleTimer: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const armStaleTimer = (): void => {
      clearTimeout(staleTimer);
      setStale(false);
      staleTimer = setTimeout(() => {
        if (!cancelled) setStale(true);
      }, STALE_AFTER_MS);
    };

    // Pacing ticker: drain one queued frame per interval so a bundle plays back at
    // roughly capture speed instead of flashing past at network speed.
    const pacer = setInterval(() => {
      if (cancelled) return;
      const next = queueRef.current.shift();
      if (next !== undefined) {
        setFrame(next);
        armStaleTimer();
      }
    }, FRAME_INTERVAL_MS);

    const scheduleReconnect = (): void => {
      if (cancelled) return;
      setReconnecting(true);
      const delay = Math.min(RETRY_MAX_MS, retryBaseMs * 2 ** Math.min(attemptsRef.current, 5));
      attemptsRef.current += 1;
      retryTimer = setTimeout(() => {
        if (!cancelled) void connect();
      }, delay);
    };

    const connect = async (): Promise<void> => {
      try {
        const { stream_url } = await api.createViewToken(cameraId);
        if (cancelled) return;
        setError(null);
        source = new EventSource(stream_url);
        source.onmessage = (ev: MessageEvent) => {
          if (cancelled) return;
          attemptsRef.current = 0;
          setReconnecting(false);
          queueRef.current.push(typeof ev.data === "string" ? ev.data : String(ev.data));
          // Live view is best-effort: under backlog keep the freshest frames.
          while (queueRef.current.length > MAX_QUEUED_FRAMES) {
            queueRef.current.shift();
          }
          armStaleTimer();
        };
        source.onerror = () => {
          // A dropped stream: the single-use token expired or the server had a
          // transient failure. Tear down and re-mint; never give up while the
          // page is open.
          source?.close();
          source = null;
          scheduleReconnect();
        };
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) {
          setError(e.body?.error ?? "Live view is not available for this camera.");
          return;
        }
        scheduleReconnect();
      }
    };

    // One-shot live warmup (prod POST /v1/live/{id}/warmup): asks the edge for a
    // short live force_sample so the tee has frames within seconds of opening the
    // page. Own token (nonces are single-use); failures are ignored — the stream
    // works without it, just darker until the next motion period.
    const warmup = async (): Promise<void> => {
      try {
        const { stream_url } = await api.createViewToken(cameraId);
        const url = stream_url.replace(`/v1/live/${cameraId}?`, `/v1/live/${cameraId}/warmup?`);
        if (url !== stream_url) await fetch(url, { method: "POST" });
      } catch {
        // best-effort
      }
    };
    void warmup();
    void connect();

    return () => {
      cancelled = true;
      source?.close();
      clearTimeout(staleTimer);
      clearTimeout(retryTimer);
      clearInterval(pacer);
      queueRef.current = [];
    };
  }, [cameraId, retryBaseMs]);

  return (
    <div
      className={styles.player}
      data-stale={stale || !frame}
      style={
        frameAspect ? ({ "--frame-aspect": String(frameAspect) } as React.CSSProperties) : undefined
      }
    >
      {frame ? (
        <img
          className={styles.frame}
          src={frameSrc(frame)}
          alt={`${cameraName} latest motion frame`}
          onLoad={(e) => {
            const el = e.currentTarget;
            if (el.naturalWidth > 0 && el.naturalHeight > 0) {
              setFrameAspect(el.naturalWidth / el.naturalHeight);
            }
          }}
        />
      ) : (
        <div className={styles.placeholder}>
          {error ??
            (reconnecting
              ? "Reconnecting to live view…"
              : "Waiting for motion — the feed only updates when something moves.")}
        </div>
      )}

      <div className={styles.overlayTop}>
        <span className={styles.motionTag} data-stale={stale || !frame}>
          MOTION
        </span>
      </div>

      {/* No name overlay: it stamped the page's EXPECTED camera name over the
          pixels - exactly where the recorder burns the real channel title - so it
          covered the one label that can't lie. The burned-in OSD is the ground
          truth for frame attribution. */}

      {(error ?? (reconnecting ? "Reconnecting to live view…" : null)) && frame && (
        <div role="alert" className={styles.error}>
          {error ?? "Reconnecting to live view…"}
        </div>
      )}
    </div>
  );
};
